import React from "react";
import FullScreenViewer from "./FullScreenViewer";

/**
 * ShipDataGrid - Displays ship data in a grid format on the map
 * props:
 *  - shipsData: [{ ship_name, mails: [{ subject, received, attachments, ... }] }]
 *  - onClose: function() - called to close the grid
 */
export default function ShipDataGrid({ shipsData = [], onClose }) {
  const [fullScreenAttachment, setFullScreenAttachment] = React.useState(null);
  const [imagesReadyToLoad, setImagesReadyToLoad] = React.useState(new Set());

  // Helper: normalize filename
  const norm = (s = "") => s.toLowerCase();

  // Extract date from filename (format: -DDMMYY or -DDMMYY.)
  const extractDateFromFilename = (filename) => {
    if (!filename) return null;
    // Match pattern like -041125 or -041125. in filename
    const match = filename.match(/-(\d{6})(?:\.|$)/);
    if (!match) return null;
    
    const dateStr = match[1]; // e.g., "041125"
    const day = parseInt(dateStr.substring(0, 2), 10);
    const month = parseInt(dateStr.substring(2, 4), 10);
    const year = parseInt(dateStr.substring(4, 6), 10);
    
    // Convert 2-digit year to 4-digit (assuming 2000-2099)
    const fullYear = year < 50 ? 2000 + year : 1900 + year;
    
    // Validate date
    if (day < 1 || day > 31 || month < 1 || month > 12) return null;
    
    try {
      const date = new Date(fullYear, month - 1, day);
      // Check if date is valid
      if (date.getFullYear() === fullYear && date.getMonth() === month - 1 && date.getDate() === day) {
        return date;
      }
    } catch (e) {
      return null;
    }
    return null;
  };

  // Parse received date string to Date object
  const parseReceivedDate = (receivedStr) => {
    if (!receivedStr) return null;
    try {
      // Try parsing as ISO date string or standard date string
      const date = new Date(receivedStr);
      if (!isNaN(date.getTime())) {
        return date;
      }
    } catch (e) {
      return null;
    }
    return null;
  };

  // Format date for display
  const formatDate = (date) => {
    if (!date) return "N/A";
    if (date instanceof Date) {
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    }
    return date.toString();
  };

  // Format graph date from filename as DD.MM.YYYY (e.g., "11.11.2025")
  const formatGraphDate = (date) => {
    if (!date || !(date instanceof Date)) return null;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  // Get header text for attachment: graph date + graph image name
  const getAttachmentHeader = (attachment) => {
    const graphDate = extractDateFromFilename(attachment.filename);
    const formattedDate = graphDate ? formatGraphDate(graphDate) : null;
    const imageName = attachment.filename || "Unknown";
    
    if (formattedDate) {
      return `${formattedDate} - ${imageName}`;
    }
    return imageName;
  };

  // Strict regex helpers for noon1 / noon2 detection
  const isNoon1Name = (filenameLower) => {
    // matches: noon1, noon-1, noon_1, graphnoon1, graph_noon_1, noon 1, etc.
    return /(?:\bnoon[\s_-]*1\b)|(?:\bgraph[\s_-]*noon[\s_-]*1\b)|(?:\bnoon1\b)/.test(
      filenameLower
    );
  };

  const isNoon2Name = (filenameLower) => {
    return /(?:\bnoon[\s_-]*2\b)|(?:\bgraph[\s_-]*noon[\s_-]*2\b)|(?:\bnoon2\b)/.test(
      filenameLower
    );
  };

  const isCIIName = (filenameLower) => {
    // Graph CII: match 'graphcii', 'graph cii', 'graph_cii', 'graph-cii', 'cii' but not 'noon' (avoid noon-related CIIs)
    // More flexible pattern to handle various filename formats
    // Match: graphcii, graph cii, graph_cii, graph-cii, or standalone cii (but not noon-related)
    const hasGraphCII = /graph[\s_-]*cii|graphcii|\bcii\b/.test(filenameLower);
    const hasNoon = /\bnoon\b/.test(filenameLower);
    // Exclude if it contains "noon" (to avoid matching noon-related CII files)
    return hasGraphCII && !hasNoon;
  };

  // Filter attachments by type using the strict helpers
  // Memoize these functions to avoid dependency issues
  const getCIIAttachments = React.useCallback((attachments) => {
    if (!attachments || attachments.length === 0) return [];
    return attachments.filter((att) => isCIIName(norm(att.filename || "")));
  }, []);

  const getNoon1Attachments = React.useCallback((attachments) => {
    if (!attachments || attachments.length === 0) return [];
    return attachments.filter((att) => {
      const f = norm(att.filename || "");
      // match noon1 explicitly, and make sure it does NOT match noon2
      return isNoon1Name(f) && !isNoon2Name(f);
    });
  }, []);

  const getNoon2Attachments = React.useCallback((attachments) => {
    if (!attachments || attachments.length === 0) return [];
    return attachments.filter((att) => {
      const f = norm(att.filename || "");
      return isNoon2Name(f) && !isNoon1Name(f);
    });
  }, []);

  // Helper to check if attachment is an image (by mimeType or file extension)
  const isImageFile = (att) => {
    const contentType = att.content_type || att.mimeType || "";
    const filename = (att.filename || "").toLowerCase();
    
    // Check mimeType first
    if (contentType.startsWith("image/")) {
      return true;
    }
    
    // Fallback: check file extension for common image formats
    const imageExtensions = [".png", ".jpg", ".jpeg", ".gif", ".bmp", ".webp", ".svg"];
    return imageExtensions.some(ext => filename.endsWith(ext));
  };

  // Get display date for an attachment (uses filename date if older for graph CII)
  const getAttachmentDisplayDate = (attachment, mail) => {
    // Check if this is a graph CII attachment
    const isCII = isCIIName(norm(attachment.filename || ""));
    
    if (isCII) {
      const filenameDate = extractDateFromFilename(attachment.filename);
      const receivedDate = parseReceivedDate(mail.received);
      
      // If we have both dates, use the older one (prefer filename date if it's older)
      if (filenameDate && receivedDate) {
        return filenameDate < receivedDate ? filenameDate : receivedDate;
      }
      
      // If we only have filename date, use it
      if (filenameDate) {
        return filenameDate;
      }
    }
    
    // For non-CII attachments or if no filename date, use received date
    const receivedDate = parseReceivedDate(mail.received);
    return receivedDate || null;
  };

  // Schedule progressive image loading after component mounts
  React.useEffect(() => {
    // Reset loaded images when shipsData changes
    setImagesReadyToLoad(new Set());
    
    // Collect all image keys that need to be loaded (matching the key format used in render)
    const imageKeys = [];
    shipsData.forEach((ship, shipIdx) => {
      if (ship.mails && ship.mails.length > 0) {
        ship.mails.forEach((mail, mailIdx) => {
          const ciiAtts = getCIIAttachments(mail.attachments || []);
          const noon1Atts = getNoon1Attachments(mail.attachments || []);
          const noon2Atts = getNoon2Attachments(mail.attachments || []);
          
          ciiAtts.forEach((att, i) => {
            if (isImageFile(att)) {
              const key = `cii-${shipIdx}-${mailIdx}-${i}-${att.filename}`;
              imageKeys.push(key);
            }
          });
          noon1Atts.forEach((att, i) => {
            if (isImageFile(att)) {
              const key = `noon1-${shipIdx}-${mailIdx}-${i}-${att.filename}`;
              imageKeys.push(key);
            }
          });
          noon2Atts.forEach((att, i) => {
            if (isImageFile(att)) {
              const key = `noon2-${shipIdx}-${mailIdx}-${i}-${att.filename}`;
              imageKeys.push(key);
            }
          });
        });
      }
    });

    // Schedule image loading with staggered delays
    const timeouts = [];
    imageKeys.forEach((key, index) => {
      const timeoutId = setTimeout(() => {
        setImagesReadyToLoad((prev) => {
          const newSet = new Set(prev);
          newSet.add(key);
          return newSet;
        });
      }, 200 + (index * 100)); // Start after 200ms, then 100ms between each image
      timeouts.push(timeoutId);
    });

    // Cleanup timeouts on unmount or when shipsData changes
    return () => {
      timeouts.forEach(timeoutId => clearTimeout(timeoutId));
    };
  }, [shipsData, getCIIAttachments, getNoon1Attachments, getNoon2Attachments]);

  // render card (unchanged except keys improved)
  const renderAttachmentCards = (attachment, mail, uniqueKey) => {
    const isImage = isImageFile(attachment);
    // Use web_path URL if available, otherwise fall back to base64 data
    const imageUrl = attachment.web_path 
      ? `https://shipreportstvm.cloud/api/${attachment.web_path}` 
      : (attachment.data ? `data:${attachment.content_type || attachment.mimeType || "image/png"};base64,${attachment.data}` : null);

    // Get the display date (uses filename date if older for graph CII)
    const displayDate = getAttachmentDisplayDate(attachment, mail);
    
    // Create a unique key for this image (matching the format in useEffect)
    const imageKey = `${uniqueKey}-${attachment.filename}`;
    const shouldLoadImage = imagesReadyToLoad.has(imageKey);

    return (
      <div
        key={uniqueKey}
        onClick={() => setFullScreenAttachment({ ...attachment, mail })}
        style={{
          padding: "10px",
          border: "2px solid #3388ff",
          borderRadius: "6px",
          cursor: "pointer",
          background: "#fff",
          transition: "all 0.2s",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          marginBottom: "8px",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.2)";
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.borderColor = "#0056b3";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.1)";
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.borderColor = "#3388ff";
        }}
      >
        <div style={{ fontWeight: "500", marginBottom: "6px", fontSize: "11px" }}>
          {getAttachmentHeader(attachment)}
        </div>
        {isImage && imageUrl ? (
          <div style={{ position: "relative", width: "100%", maxHeight: "80px", marginBottom: "6px" }}>
            {shouldLoadImage ? (
              <img
                src={imageUrl}
                alt={attachment.filename}
                onLoad={(e) => {
                  e.target.style.opacity = "1";
                }}
                style={{
                  width: "100%",
                  maxHeight: "80px",
                  objectFit: "contain",
                  borderRadius: "4px",
                  opacity: 0,
                  transition: "opacity 0.3s ease-in",
                }}
              />
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "80px",
                  background: "#f0f0f0",
                  borderRadius: "4px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div className="spinner-border spinner-border-sm" role="status" style={{ width: "16px", height: "16px", borderWidth: "2px" }}>
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div
            style={{
              padding: "20px",
              background: "#f8f9fa",
              borderRadius: "4px",
              textAlign: "center",
              marginBottom: "6px",
            }}
          >
            <i className="fas fa-file-pdf" style={{ fontSize: "32px", color: "#dc3545" }} />
            <div style={{ fontSize: "10px", marginTop: "4px" }}>{attachment.filename}</div>
          </div>
        )}
        <div style={{ fontSize: "10px", color: "#666", marginTop: "4px" }}>
          Received: {formatDate(displayDate)}
        </div>
        <div style={{ fontSize: "9px", color: "#999", marginTop: "2px" }}>Click to view full screen</div>
      </div>
    );
  };

  // Early return after all hooks
  if (!shipsData || shipsData.length === 0) {
    return null;
  }

  return (
    <>
      <div
        style={{
          position: "absolute",
          top: "10px",
          left: "10px",
          background: "white",
          borderRadius: "8px",
          padding: "15px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
          zIndex: 1000,
          maxWidth: "90%",
          maxHeight: "90%",
          overflow: "auto",
        }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 style={{ margin: 0, fontSize: "18px" }}>Ship Data</h5>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={onClose}
            style={{ padding: "4px 12px" }}
          >
            × Close
          </button>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
            <thead>
              <tr style={{ background: "#f8f9fa", borderBottom: "2px solid #dee2e6" }}>
                <th style={{ padding: "8px", textAlign: "left", border: "1px solid #dee2e6", minWidth: "200px" }}>
                  Graph CII
                </th>
                <th style={{ padding: "8px", textAlign: "left", border: "1px solid #dee2e6", minWidth: "200px" }}>
                  Graph Noon 1
                </th>
                <th style={{ padding: "8px", textAlign: "left", border: "1px solid #dee2e6", minWidth: "200px" }}>
                  Graph Noon 2
                </th>
              </tr>
            </thead>
            <tbody>
              {shipsData.map((ship, shipIdx) => {
                // Collect all attachments by type across all mails for this ship
                const allCII = [];
                const allNoon1 = [];
                const allNoon2 = [];

                if (ship.mails && ship.mails.length > 0) {
                  ship.mails.forEach((mail, mailIdx) => {
                    const ciiAtts = getCIIAttachments(mail.attachments || []);
                    const noon1Atts = getNoon1Attachments(mail.attachments || []);
                    const noon2Atts = getNoon2Attachments(mail.attachments || []);

                    ciiAtts.forEach((att, i) => allCII.push({ att, mail, key: `cii-${shipIdx}-${mailIdx}-${i}` }));
                    noon1Atts.forEach((att, i) =>
                      allNoon1.push({ att, mail, key: `noon1-${shipIdx}-${mailIdx}-${i}` })
                    );
                    noon2Atts.forEach((att, i) =>
                      allNoon2.push({ att, mail, key: `noon2-${shipIdx}-${mailIdx}-${i}` })
                    );
                  });
                }

                return (
                  <tr key={`ship-${shipIdx}`} style={{ borderBottom: "1px solid #dee2e6" }}>
                   
                    <td style={{ padding: "8px", border: "1px solid #dee2e6", verticalAlign: "top" }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        {allCII.length > 0 ? (
                          allCII.map((item) => renderAttachmentCards(item.att, item.mail, item.key))
                        ) : (
                          <div style={{ padding: "8px", color: "#999", fontSize: "11px", fontStyle: "italic" }}>
                            No GraphCII
                          </div>
                        )}
                      </div>
                    </td>

                    <td style={{ padding: "8px", border: "1px solid #dee2e6", verticalAlign: "top" }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        {allNoon1.length > 0 ? (
                          allNoon1.map((item) => renderAttachmentCards(item.att, item.mail, item.key))
                        ) : (
                          <div style={{ padding: "8px", color: "#999", fontSize: "11px", fontStyle: "italic" }}>
                            No GraphNoon1
                          </div>
                        )}
                      </div>
                    </td>

                    <td style={{ padding: "8px", border: "1px solid #dee2e6", verticalAlign: "top" }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        {allNoon2.length > 0 ? (
                          allNoon2.map((item) => renderAttachmentCards(item.att, item.mail, item.key))
                        ) : (
                          <div style={{ padding: "8px", color: "#999", fontSize: "11px", fontStyle: "italic" }}>
                            No GraphNoon2
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Full Screen Viewer */}
      {fullScreenAttachment && (
        <FullScreenViewer attachment={fullScreenAttachment} onClose={() => setFullScreenAttachment(null)} />
      )}
    </>
  );
}
